import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Checkbox, TimePicker, DatePicker } from 'antd';
import ToggleSwitch from '../../components/elements/toggleSwitch/ToggleSwitch';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import { Container } from '../../components/content';
import { FloatingLabelInput } from '../../components/elements/floatingLabelInput/FloatingLabelInput';
import scheduledNotificationApi from '../../api/scheduled-notification-api';
import TranslateButton from '../../components/elements/TranslateButton/TranslateButton';
import { useTranslateFields } from '../../hooks/useTranslateFields';
import styles from '../../styles/mobileInfoPage.module.scss';
import dayjs from 'dayjs';

const LANG_SECTIONS = [
  { key: 'sr', label: 'Srpski' },
  { key: 'hu', label: 'Mađarski' },
  { key: 'en', label: 'Engleski' },
];

const DAY_OPTIONS = [
  { label: 'Ned', value: 0 },
  { label: 'Pon', value: 1 },
  { label: 'Uto', value: 2 },
  { label: 'Sre', value: 3 },
  { label: 'Čet', value: 4 },
  { label: 'Pet', value: 5 },
  { label: 'Sub', value: 6 },
];

const emptyForm = {
  title: { sr: '', hu: '', en: '' },
  body: { sr: '', hu: '', en: '' },
  scheduleType: 'recurring',
  scheduledAt: null,
  hour: null,
  minute: null,
  daysOfWeek: [],
};

export default function DodajNotifikaciju() {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const setTitle = (lang, val) =>
    setForm((prev) => ({ ...prev, title: { ...prev.title, [lang]: val } }));

  const setBody = (lang, val) =>
    setForm((prev) => ({ ...prev, body: { ...prev.body, [lang]: val } }));

  const validate = () => {
    for (const { key, label } of LANG_SECTIONS) {
      if (!form.title[key]?.trim()) {
        toast.error(`Naslov na ${label}om je obavezan`);
        return false;
      }
      if (!form.body[key]?.trim()) {
        toast.error(`Tekst na ${label}om je obavezan`);
        return false;
      }
    }
    if (form.scheduleType === 'once') {
      if (!form.scheduledAt) {
        toast.error('Datum i vreme slanja su obavezni za jednokratne notifikacije');
        return false;
      }
      if (dayjs(form.scheduledAt).isBefore(dayjs())) {
        toast.error('Datum i vreme slanja ne mogu biti u prošlosti');
        return false;
      }
    }
    if (form.scheduleType === 'recurring' && (form.hour === null || form.minute === null)) {
      toast.error('Vreme slanja je obavezno za ponavljajuće notifikacije');
      return false;
    }
    return true;
  };

  const { translating, translate } = useTranslateFields();

  const handleTranslate = async () => {
    if (!form.title.sr?.trim() || !form.body.sr?.trim()) {
      toast.error('Popunite naslov i tekst na srpskom pre prevođenja');
      return;
    }

    const result = await translate({ title: form.title.sr, body: form.body.sr });
    if (!result) return;

    for (const lang of ['hu', 'en']) {
      setTitle(lang, result[lang].title);
      setBody(lang, result[lang].body);
    }

    toast.success('Prevod je uspešno primenjen');
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      title: form.title,
      body: form.body,
      scheduleType: form.scheduleType,
    };

    if (form.scheduleType === 'once') {
      payload.scheduledAt = form.scheduledAt.toISOString();
    } else {
      payload.hour = form.hour;
      payload.minute = form.minute;
      if (form.daysOfWeek.length > 0) payload.daysOfWeek = form.daysOfWeek;
    }

    const { success, message } = await scheduledNotificationApi.create(payload);
    if (success) {
      toast.success('Notifikacija kreirana');
      navigate('/notifikacije');
    } else {
      toast.error(message || 'Greška pri kreiranju notifikacije');
    }
    setSaving(false);
  };

  return (
    <Container>
      <Link to="/notifikacije" className="link_btn" style={{ marginBottom: '12px' }}>
        <img alt="back" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>

      <ContentLayout pt={true}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <h2 className={styles.pageTitle} style={{ margin: 0 }}>Nova notifikacija</h2>
          <Button className="btn-primary" onClick={handleSave} loading={saving}>
            Kreiraj notifikaciju
          </Button>
        </div>

        {/* Title + Body side by side */}
        <section className={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <TranslateButton onClick={handleTranslate} loading={translating} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
            <div>
              <h3 className={styles.sectionTitle}>Naslov</h3>
              <div className={styles.fieldStack}>
                {LANG_SECTIONS.map(({ key, label }) => (
                  <FloatingLabelInput
                    key={key}
                    label={`Naslov — ${label}`}
                    value={form.title[key]}
                    onChange={(e) => setTitle(key, e.target.value)}
                    placeholder={`Unesite naslov na ${label.toLowerCase()}om`}
                  />
                ))}
              </div>
            </div>
            <div>
              <h3 className={styles.sectionTitle}>Tekst</h3>
              <div className={styles.fieldStack}>
                {LANG_SECTIONS.map(({ key, label }) => (
                  <FloatingLabelInput
                    key={key}
                    label={`Tekst — ${label}`}
                    value={form.body[key]}
                    onChange={(e) => setBody(key, e.target.value)}
                    placeholder={`Unesite tekst na ${label.toLowerCase()}om`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Schedule type */}
        <section className={styles.section}>
          <div>
            <h3 className={styles.sectionTitle}>Tip rasporeda</h3>
            <ToggleSwitch
              checked={form.scheduleType === 'once'}
              onChange={(e) => setForm((prev) => ({ ...prev, scheduleType: e.target.checked ? 'once' : 'recurring', scheduledAt: null, hour: null, minute: null, daysOfWeek: [] }))}
              label={form.scheduleType === 'once' ? 'Jednokratno' : 'Ponavljajuće'}
            />
          </div>
        </section>

        {/* Date / Time + Days */}
        <section className={styles.section}>
          {form.scheduleType === 'once' ? (
            <>
              <h3 className={styles.sectionTitle}>Datum i vreme slanja</h3>
              <DatePicker
                showTime
                format="DD.MM.YYYY. HH:mm"
                placeholder="Izaberite datum i vreme"
                value={form.scheduledAt ? dayjs(form.scheduledAt) : null}
                onChange={(val) => setForm((prev) => ({ ...prev, scheduledAt: val ? val.toDate() : null }))}
                disabledDate={(d) => d && d.isBefore(dayjs(), 'day')}
                style={{ height: '47px' }}
              />
            </>
          ) : (
            <>
              <h3 className={styles.sectionTitle}>Vreme slanja</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '24px', alignItems: 'stretch' }}>
                <TimePicker
                  format="HH:mm"
                  placeholder="Izaberite vreme"
                  value={form.hour !== null && form.minute !== null ? dayjs().hour(form.hour).minute(form.minute) : null}
                  onChange={(val) =>
                    setForm((prev) => ({
                      ...prev,
                      hour: val ? val.hour() : null,
                      minute: val ? val.minute() : null,
                    }))
                  }
                  style={{ height: '47px' }}
                />
                <div>
                  <p className={styles.fieldLabel} style={{ marginBottom: '8px' }}>Dani u nedelji (prazno = svaki dan)</p>
                  <Checkbox.Group
                    options={DAY_OPTIONS}
                    value={form.daysOfWeek}
                    onChange={(vals) => setForm((prev) => ({ ...prev, daysOfWeek: vals }))}
                  />
                </div>
              </div>
            </>
          )}
        </section>

      </ContentLayout>
    </Container>
  );
}
